import React, { use, useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { config, Config } from '../utils/config';
import { AppContext } from '../utils/context';
import { useInputFieldSetter } from '../utils/hooks';
import { logError } from '../utils/misc';

interface CustomRouteHandlerOptions {
    readonly page: keyof Config['captcha'];
    readonly onParamChange: (parameters: Record<string, string>) => void;
    readonly resetFactor: unknown;
}

interface CaptchaResponse {
    ok: boolean;
    time: number;
    id: string;
    token: string;
    image: string;
}

const invalidCaptchaResponse = {
    ok: false,
    time: 0,
    id: '',
    token: '',
    image: '',
};

function makeCaptchaImage(data: CaptchaResponse | undefined, isDataLoading: boolean) {
    if (data?.image) {
        return <img alt="CAPTCHA" src={data.image} />;
    }

    return isDataLoading ? <h3>Loading</h3> : <h3>Error! Click refresh</h3>;
}

export const CaptchaContainer: React.FC<CustomRouteHandlerOptions> = ({ page, onParamChange, resetFactor }) => {
    const isEnabled = config.captcha[page];

    const { apiAccessor } = use(AppContext);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [data, setData] = useState<CaptchaResponse | undefined>();

    const onResponseChangeCallback = useCallback(
        (response: string) => {
            if (!data) {
                return;
            }

            onParamChange({
                captchaResponse: response,
                captchaTime: data.time.toString(),
                captchaId: data.id,
                captchaToken: data.token,
            });
        },
        [data, onParamChange],
    );

    const [response, setResponse, setResponseText] = useInputFieldSetter('', onResponseChangeCallback);

    const setReload = useCallback(() => {
        if (!isEnabled) {
            onParamChange({
                captchaResponse: 'disabled',
            });

            return;
        }

        setResponseText('');
        onParamChange({});
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setData(undefined);
    }, [isEnabled, setData, onParamChange, setResponseText]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setReload();
    }, [resetFactor, setReload]);

    useEffect(() => {
        if (!isEnabled) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setReload();
        }
    }, [isEnabled, setReload]);

    useEffect(() => {
        if (isDataLoading || !!data) {
            return;
        }

        // eslint-disable-next-line react-hooks/set-state-in-effect, @eslint-react/set-state-in-effect
        setIsDataLoading(true);

        void (async () => {
            try {
                const newData = await apiAccessor.fetch(`/api/v1/system/captcha/${page}`, {
                    method: 'POST',
                });
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                setData(newData as CaptchaResponse);
                setIsDataLoading(false);
            } catch (error: unknown) {
                logError(error);
                setData(invalidCaptchaResponse);
                setIsDataLoading(false);
            }
        })();
    }, [resetFactor, isEnabled, data, isDataLoading, apiAccessor, setIsDataLoading, page]);

    if (!isEnabled) {
        return null;
    }

    return (
        <Row>
            <Col md="auto">{makeCaptchaImage(data, isDataLoading)}</Col>
            <Col md="auto">
                <Button onClick={setReload}>Reload</Button>
            </Col>
            <Col>
                <FloatingLabel className="w-100" label="Enter the characters you see">
                    <Form.Control
                        disabled={!data}
                        name="response"
                        onChange={setResponse}
                        placeholder=""
                        required
                        type="text"
                        value={response}
                    />
                </FloatingLabel>
            </Col>
        </Row>
    );
};
